import { FC, useEffect } from 'react'

import { useFormik } from 'formik'

import { ClienteView } from '../../../empresasCliente-list/core/_models'
import { ClienteDtoColaborador, ColaboradorList } from '../../core/_models'
import { updateCoordernador, createCoordenador } from '../../core/_request'
import {
  Button,
  FetchState,
  FormInput,
  FormSelect,
  IOption,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import {
  ButtonType,
  MaskType,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { getEstadoByUf } from '../../../../atendimento_medicos/atendimentos-medico-list/core/_requests'
import { coordenadorSchema } from './validation/coordenadorSchema'
import { useT } from '_metronic/i18n/Metronici18n'

export const CoordenadorClienteModal: FC = () => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: coordenador } = (fetchStates['coordenador'] || {}) as FetchState<ColaboradorList>
  const { data: cliente } = (fetchStates['cliente'] || {}) as FetchState<ClienteView>

  const initialValues = {
    nome: coordenador?.nome || '',
    crm: coordenador?.crm || '',
    rqe: coordenador?.rqe || '',
    uf: undefined as IOption<string> | undefined,
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: coordenadorSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (coordenador) {
          const coord: ClienteDtoColaborador = {
            id: coordenador?.id as number,
            nome: values.nome,
            crm: values.crm,
            rqe: values.rqe,
            uf: values.uf?.value as string,
          }
          await updateCoordernador(cliente?.id, coord)
        } else {
          const coord: ClienteDtoColaborador = {
            nome: values.nome,
            crm: values.crm,
            rqe: values.rqe,
            uf: values.uf?.value as string,
          }
          await createCoordenador(cliente?.id, coord)
        }
        await showAlert({
          title: 'Sucesso!',
          text: coordenador
            ? 'Coordenador atualizado com sucesso!'
            : 'Coordenador adicionado com sucesso!',
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const { setFieldValue } = formik

  useEffect(() => {
    const populateUf = async () => {
      if (coordenador) {
        const uf = await getEstadoByUf(coordenador.uf)

        setFieldValue('uf', {
          label: uf.descricao,
          value: uf.sigla,
        })
      }
    }

    populateUf()
  }, [coordenador, setFieldValue])

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormInput
        formik={formik}
        label='Nome'
        name='nome'
        placeholder='Digite o nome'
        isRequired={true}
        className='mb-3'
      />
      <FormInput
        formik={formik}
        label='Número do CRM'
        name='crm'
        placeholder='Número do Conselho de Classe'
        mask={MaskType.CRM}
        isRequired={true}
        className='mb-3'
      />
      <FormSelect
        formik={formik}
        fetchOptions={OptionsHelper.getOptionsEstadosUf}
        name='uf'
        label='Estado do CRM'
        placeholder='Selecione um estado'
        isClearable={false}
        isRequired={true}
        className='mb-3'
      />
      <FormInput
        formik={formik}
        label='Registro de Qualificação de Especialista (RQE)'
        name='rqe'
        placeholder='RQE'
        mask={MaskType.RQE}
        className='mb-3'
      />

      <div className='d-flex justify-content-center mt-10'>
        <Button type={ButtonType.Reset} className='btn-secondary me-3' onClick={closeModal}>
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary'>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.send')}
        </Button>
      </div>
    </form>
  )
}
